import type { CartArticleData } from '@hub/bearing-hub/common/basket/useAddToShoppingCart';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { getLocale } from '@hub/libs/constants';
import { EXTERNAL_LINKS } from '@hub/libs/redirect.helper';
import { getEventCategory, TRACKED_STANDARD_EVENT_ACTIONS } from '@hub/libs/tracking';
import type { StandardAction } from '@hub/libs/tracking';
import type { IbhShape } from '@igus/iglidur-bearing-hub-api';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

type BasketToastSuccessContentType = {
  articleData: CartArticleData;
  shape: IbhShape;
};
export const BasketToastSuccessContent = ({ articleData, shape }: BasketToastSuccessContentType) => {
  const { t } = useTranslation(['common', 'product/buyBox']);
  const { CustomEvent } = usePiwikPro();
  const router = useRouter();
  const locale = getLocale(router);
  const { quantity, material, unitPrice, totalPrice } = articleData;

  const trackEvent = (trackingEvent: StandardAction) => {
    const eventCategory = getEventCategory(router.asPath);
    if (eventCategory) {
      CustomEvent.trackEvent(eventCategory, trackingEvent);
    }
  };

  return (
    <div className="flex flex-row items-center" data-testid="add-to-basket-success-toast">
      <div className="mr-4">
        <Icon size={9} id={IconID.INFO_CIRCLE_MODERN_FILLED} color={IconColor.WHITE} />
      </div>

      <div className="flex-col">
        <p className="mb-2 font-normal text-xl">
          {t('NOTIFICATION.ADD_TO_CART_SUCCESS', {
            quantity,
            material: material.name,
            shape: t(`SHAPES.${shape}.SHAPE_MENU`, { ns: 'bearing-hub/bearingHub' }),
            ns: 'bearing-hub/bearingHub',
          })}
        </p>
        {unitPrice.value !== -1 && (
          <>
            <p className="font-semibold text-xl">
              {unitPrice.value.toFixed(2)} {unitPrice.unit}/
              {t('NOTIFICATION.UNIT_PRICE', { ns: 'bearing-hub/bearingHub' })}
            </p>
            <p className="font-semibold text-xl">
              {totalPrice.value.toFixed(2)} {totalPrice.unit}/
              {t('NOTIFICATION.TOTAL_PRICE', { ns: 'bearing-hub/bearingHub' })}
            </p>
          </>
        )}
        <a
          href={EXTERNAL_LINKS.CART(locale)}
          data-testid="goto-shopping-cart"
          className="flex underline text-xl font-semibold pt-2"
          onClick={() => trackEvent(TRACKED_STANDARD_EVENT_ACTIONS.goToShoppingBasket)}
        >
          {t('BUY_BOX.ADD_TO_SHOPPING_CART_SUCCESS_MODAL_CART_BUTTON', { ns: 'product/buyBox' })}
        </a>
      </div>
    </div>
  );
};
